import { useEffect, useMemo, useState } from 'react';
import Header from '../components/header/Header';
import SideMenu from '../components/navigation/SideMenu';
import AppNavbar from '../components/navigation/AppNavbar';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import { StyledDataGrid } from '../components/customizations/dataGrid';
import { useAuth } from '../authentication/AuthContext';
import axiosInstance from '../authentication/AuthContext';

export default function ActivityLogs() {
    const { accessToken } = useAuth();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedUser, setSelectedUser] = useState('all');

    useEffect(() => {
        const fetchActivity = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axiosInstance.get('/accounts/activity');
                const data = response.data;

                let parsed = [];
                if (Array.isArray(data)) {
                    parsed = data;
                } else if (Array.isArray(data?.results)) {
                    parsed = data.results;
                } else if (Array.isArray(data?.activities)) {
                    parsed = data.activities;
                } else if (Array.isArray(data?.data)) {
                    parsed = data.data;
                }

                setActivities(parsed);
            } catch (err) {
                console.error('Failed to fetch activity logs:', err);
                setError(
                    err.response?.data?.detail || 'Failed to load activity logs'
                );
                setActivities([]);
            } finally {
                setLoading(false);
            }
        };

        if (accessToken) {
            fetchActivity();
        }
    }, [accessToken]);

    const normalizedRows = useMemo(
        () =>
            activities.map((activity, index) => {
                const timestamp =
                    activity.timestamp ??
                    activity.created_at ??
                    activity.createdAt ??
                    activity.datetime ??
                    activity.date ??
                    null;
                const userValue =
                    activity.user?.username ??
                    activity.user?.email ??
                    activity.user?.name ??
                    activity.username ??
                    activity.email ??
                    activity.user_name ??
                    (activity.user_id != null
                        ? `User #${activity.user_id}`
                        : 'Unknown');

                return {
                    id: activity.id ?? `activity-${index}`,
                    user: userValue,
                    action:
                        activity.action ??
                        activity.activity ??
                        activity.activity_type ??
                        activity.type ??
                        '—',
                    ip_address: activity.ip_address ?? activity.ip ?? '—',
                    target:
                        activity.target ??
                        activity.object ??
                        activity.resource ??
                        activity.endpoint ??
                        '—',
                    details:
                        typeof activity.details === 'string'
                            ? activity.details
                            : typeof activity.description === 'string'
                              ? activity.description
                              : '—',
                    timestamp,
                    __raw: activity,
                };
            }),
        [activities]
    );

    const userOptions = useMemo(() => {
        const uniqueUsers = Array.from(
            new Set(normalizedRows.map((row) => row.user).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        return ['all', ...uniqueUsers];
    }, [normalizedRows]);

    const filteredRows = useMemo(() => {
        if (selectedUser === 'all') return normalizedRows;
        return normalizedRows.filter((row) => row.user === selectedUser);
    }, [normalizedRows, selectedUser]);

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const monthLabel = now.toLocaleDateString(undefined, {
        month: 'long',
        year: 'numeric',
    });

    const heatmapCounts = useMemo(() => {
        const matrix = Array.from({ length: 24 }, () =>
            Array.from({ length: daysInMonth }, () => 0)
        );

        filteredRows.forEach((row) => {
            if (!row.timestamp) return;
            const date = new Date(row.timestamp);
            if (Number.isNaN(date.getTime())) return;
            if (
                date.getFullYear() !== currentYear ||
                date.getMonth() !== currentMonth
            )
                return;

            const dayIndex = date.getDate() - 1;
            const hourIndex = date.getHours();
            if (
                dayIndex >= 0 &&
                dayIndex < daysInMonth &&
                hourIndex >= 0 &&
                hourIndex < 24
            ) {
                matrix[hourIndex][dayIndex] += 1;
            }
        });

        return matrix;
    }, [filteredRows, currentYear, currentMonth, daysInMonth]);

    const maxHeatValue = useMemo(
        () => Math.max(0, ...heatmapCounts.flat()),
        [heatmapCounts]
    );

    const activityColumns = [
        { field: 'user', headerName: 'User', flex: 1, minWidth: 160 },
        { field: 'action', headerName: 'Activity', flex: 1, minWidth: 160 },
        { field: 'target', headerName: 'Target', flex: 1, minWidth: 150 },
        {
            field: 'ip_address',
            headerName: 'IP Address',
            flex: 1,
            minWidth: 140,
        },
        { field: 'details', headerName: 'Details', flex: 1.5, minWidth: 220 },
        {
            field: 'timestamp',
            headerName: 'Timestamp',
            flex: 1,
            minWidth: 180,
            renderCell: (params) =>
                params.value ? new Date(params.value).toLocaleString() : '—',
        },
    ];

    const getHeatmapColor = (value) => {
        if (maxHeatValue === 0 || value === 0)
            return 'rgba(25, 118, 210, 0.08)';
        const intensity = value / maxHeatValue;
        return `rgba(25, 118, 210, ${0.15 + intensity * 0.8})`;
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <SideMenu />
            <AppNavbar />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    bgcolor: 'background.paper',
                }}
            >
                <Header />
                <Box
                    sx={{
                        flex: 1,
                        width: '100%',
                        maxWidth: '100%',
                        px: { xs: 2, sm: 3 },
                        py: { xs: 2, sm: 3 },
                        pb: 5,
                        mt: { xs: 8, md: 0 },
                    }}
                >
                    {loading && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {!loading && !error && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                            }}
                        >
                            {/* Activity Heatmap */}
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Activity Heatmap ({monthLabel})
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 2 }}
                                >
                                    X-axis is day of month, Y-axis is hour of
                                    day.
                                </Typography>
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Box
                                        sx={{
                                            minWidth: Math.max(
                                                900,
                                                daysInMonth * 24
                                            ),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                mb: 0.5,
                                                pl: 7,
                                            }}
                                        >
                                            {Array.from(
                                                { length: daysInMonth },
                                                (_, idx) => (
                                                    <Box
                                                        key={`day-label-${idx + 1}`}
                                                        sx={{
                                                            width: 20,
                                                            textAlign: 'center',
                                                            fontSize: 10,
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        {idx + 1}
                                                    </Box>
                                                )
                                            )}
                                        </Box>

                                        <Divider sx={{ mb: 1 }} />

                                        {heatmapCounts.map(
                                            (hourValues, hour) => (
                                                <Box
                                                    key={`hour-row-${hour}`}
                                                    sx={{
                                                        display: 'flex',
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 56,
                                                            pr: 1,
                                                            textAlign: 'right',
                                                            fontSize: 11,
                                                            color: 'text.secondary',
                                                            lineHeight: '18px',
                                                        }}
                                                    >
                                                        {`${String(hour).padStart(2, '0')}:00`}
                                                    </Box>
                                                    {hourValues.map(
                                                        (count, dayIndex) => (
                                                            <Box
                                                                key={`cell-${hour}-${dayIndex}`}
                                                                title={`Day ${dayIndex + 1}, ${String(hour).padStart(2, '0')}:00 — ${count} activities`}
                                                                sx={{
                                                                    width: 20,
                                                                    height: 18,
                                                                    mr: 0.5,
                                                                    borderRadius: 0.3,
                                                                    backgroundColor:
                                                                        getHeatmapColor(
                                                                            count
                                                                        ),
                                                                }}
                                                            />
                                                        )
                                                    )}
                                                </Box>
                                            )
                                        )}
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        gap: 1,
                                        mt: 1.5,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Less
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 18,
                                            height: 12,
                                            bgcolor: 'rgba(25, 118, 210, 0.08)',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            width: 18,
                                            height: 12,
                                            bgcolor: 'rgba(25, 118, 210, 0.35)',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            width: 18,
                                            height: 12,
                                            bgcolor: 'rgba(25, 118, 210, 0.95)',
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        More
                                    </Typography>
                                </Box>
                            </Paper>

                            {/* User Activity Logs */}
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: {
                                            xs: 'stretch',
                                            sm: 'center',
                                        },
                                        flexDirection: {
                                            xs: 'column',
                                            sm: 'row',
                                        },
                                        gap: 2,
                                        mb: 2,
                                    }}
                                >
                                    <Typography variant="h6">
                                        User Activity Logs (
                                        {filteredRows.length})
                                    </Typography>
                                    <FormControl
                                        size="small"
                                        sx={{ minWidth: 220 }}
                                    >
                                        <InputLabel id="activity-user-filter-label">
                                            Filter by user
                                        </InputLabel>
                                        <Select
                                            labelId="activity-user-filter-label"
                                            value={selectedUser}
                                            label="Filter by user"
                                            onChange={(event) =>
                                                setSelectedUser(
                                                    event.target.value
                                                )
                                            }
                                        >
                                            {userOptions.map((user) => (
                                                <MenuItem
                                                    key={user}
                                                    value={user}
                                                >
                                                    {user === 'all'
                                                        ? 'All users'
                                                        : user}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <StyledDataGrid
                                    rows={filteredRows}
                                    columns={activityColumns}
                                />
                            </Paper>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
